import {
  CaretDownFilled,
  CloseCircleFilled,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import {
  Button,
  Checkbox,
  Flex,
  Input,
  Layout,
  Modal,
  Select,
  Table,
  Tag,
  Typography,
} from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { STATE_ABBREVIATIONS } from 'constants/stateAbbreviations';
import { usStates } from 'constants/states';

import CreatePodModal from './components/create-pod-modal';
import PodCellPopover from './components/pod-cell-popover';
import PodEmptyState from './components/pod-empty-state';
import PodRowActions from './components/pod-row-actions';
import { usePodFilters } from './hooks/use-pod-filters';
import { usePods } from './hooks/use-pod-management';
import { useDeletePod } from './hooks/use-delete-pod';
import { useTogglePodStatus } from './hooks/use-toggle-pod-status';
import s from './styles.module.scss';

import type { ColumnsType, TableProps } from 'antd/es/table';
import type { Pod, PodFilters, PodSortField } from './types';

const { Header } = Layout;
const { Text } = Typography;

const stateOptions = usStates.map((state) => ({
  label: `${state.label} (${STATE_ABBREVIATIONS[state.value] ?? ''})`,
  value: state.value,
}));

const POD_TYPE_CONFIG: Record<
  string,
  { bg: string; text: string; label: string }
> = {
  RPM: { bg: '#fff7e6', text: '#d46b08', label: 'labels.rpm' },
  CCM: { bg: '#e6f4ff', text: '#0958d9', label: 'labels.ccm' },
};
const DEFAULT_POD_TYPE_CONFIG = {
  bg: '#fafafa',
  text: 'rgba(0,0,0,0.88)',
  label: '',
};

const STATUS_CONFIG: Record<
  string,
  { bg: string; text: string; border: string; label: string }
> = {
  active: {
    bg: '#f6ffed',
    text: '#389E0D',
    border: '#95de64',
    label: 'labels.active',
  },
  inactive: {
    bg: '#fafafa',
    text: 'rgba(0,0,0,0.88)',
    border: '#d9d9d9',
    label: 'labels.inactive',
  },
};

const PodManagement = () => {
  const { t } = useTranslation();
  const { filter: podFilters, updateFilter } = usePodFilters();
  const {
    pods,
    isLoading,
    isFetching,
    isError,
    refetch,
    pagination,
    updateFilters,
    filters,
  } = usePods(podFilters);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editPodGuid, setEditPodGuid] = useState<string | null>(null);
  const [highlightedPodGuid, setHighlightedPodGuid] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (!highlightedPodGuid) return;
    const timer = window.setTimeout(() => setHighlightedPodGuid(null), 3000);
    return () => window.clearTimeout(timer);
  }, [highlightedPodGuid]);

  const isModalOpen = isCreateModalOpen || !!editPodGuid;

  const handleEditPod = (pod: Pod) => {
    if (pod.status === 'inactive') return;
    setEditPodGuid(pod.guid);
  };

  const isStatusConfirmShowingRef = useRef(false);
  const { toggleStatus } = useTogglePodStatus();

  const isDeleteConfirmShowingRef = useRef(false);
  const { deletePod } = useDeletePod();

  const handleInactivatePod = (pod: Pod) => {
    if (isStatusConfirmShowingRef.current) return;
    isStatusConfirmShowingRef.current = true;

    Modal.confirm({
      title: t('labels.inactivatePod'),
      content: t('labels.inactivatePodBody'),
      okText: t('labels.inactivatePodButton'),
      cancelText: t('labels.cancel'),
      autoFocusButton: 'cancel',
      centered: true,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        return new Promise<void>((resolve) => {
          toggleStatus(pod.guid, 'inactive', {
            onSettled: () => resolve(),
          });
        });
      },
      afterClose: () => {
        isStatusConfirmShowingRef.current = false;
      },
    });
  };

  const handleActivatePod = (pod: Pod) => {
    if (isStatusConfirmShowingRef.current) return;
    isStatusConfirmShowingRef.current = true;

    Modal.confirm({
      title: t('labels.activatePod'),
      content: t('labels.activatePodBody'),
      okText: t('labels.activatePodButton'),
      cancelText: t('labels.cancel'),
      autoFocusButton: 'cancel',
      centered: true,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        return new Promise<void>((resolve) => {
          toggleStatus(pod.guid, 'active', {
            onSettled: () => resolve(),
          });
        });
      },
      afterClose: () => {
        isStatusConfirmShowingRef.current = false;
      },
    });
  };

  const handleDeletePod = (pod: Pod) => {
    if (isDeleteConfirmShowingRef.current) return;
    isDeleteConfirmShowingRef.current = true;

    Modal.confirm({
      icon: <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
      title: t('labels.deletePod'),
      content: t('labels.deletePodBody'),
      okText: t('labels.deletePodButton'),
      cancelText: t('labels.cancel'),
      autoFocusButton: 'cancel',
      centered: true,
      width: 480,
      rootClassName: 'discard-changes-confirm',
      onOk: () => {
        return new Promise<void>((resolve) => {
          deletePod(pod.guid, {
            onSettled: () => resolve(),
          });
        });
      },
      afterClose: () => {
        isDeleteConfirmShowingRef.current = false;
      },
    });
  };

  // Bridge: sync URL filter changes into usePods internal state
  useEffect(() => {
    updateFilters(podFilters);
  }, [podFilters, updateFilters]);

  // Search: local state for immediate input feedback (debounce buffer)
  const [searchValue, setSearchValue] = useState(podFilters.search ?? '');

  // Search: debounce 300ms — commit to URL after typing stops
  useEffect(() => {
    const trimmedSearch = searchValue.trim() || undefined;
    if (trimmedSearch === podFilters.search) return;

    const timeoutId = window.setTimeout(() => {
      updateFilter({ search: trimmedSearch });
    }, 300);

    return () => window.clearTimeout(timeoutId);
  }, [searchValue, podFilters.search, updateFilter]);

  // Search: sync-back for browser navigation / external URL changes
  useEffect(() => {
    setSearchValue(podFilters.search ?? '');
  }, [podFilters.search]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    updateFilter({ search: undefined });
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const trimmedSearch = searchValue.trim() || undefined;
      updateFilter({ search: trimmedSearch });
    }
  };

  const handlePodTypeChange = (value: string | undefined) => {
    // Safe: Select options constrain possible values to 'RPM' | 'CCM' | undefined (allowClear / "All Types")
    updateFilter({ podType: (value || undefined) as PodFilters['podType'] });
  };
  const handleStatusChange = (value: string | undefined) => {
    // Safe: Select options constrain possible values to 'active' | 'inactive' | undefined (allowClear / "All Status")
    updateFilter({ status: (value || undefined) as PodFilters['status'] });
  };
  const handleStatesChange = (value: string[]) => {
    updateFilter({ states: value.length ? value : undefined });
  };
  const handleClearStates = () => {
    updateFilter({ states: undefined });
  };

  const hasActiveFilters = !!(
    podFilters.podType ||
    podFilters.status ||
    podFilters.states?.length ||
    podFilters.search
  );

  const handleClearAllFilters = () => {
    updateFilter({
      podType: undefined,
      status: undefined,
      states: undefined,
      search: undefined,
    });
    setSearchValue('');
  };

  const emptyText = (
    <PodEmptyState
      hasActiveFilters={hasActiveFilters}
      onClearFilters={handleClearAllFilters}
      onCreatePod={() => setIsCreateModalOpen(true)}
    />
  );

  const [stateSearch, setStateSearch] = useState('');
  const filteredStateOptions = stateSearch
    ? stateOptions.filter((opt) =>
        opt.label.toLowerCase().includes(stateSearch.toLowerCase()),
      )
    : stateOptions;

  const getSortOrder = (field: PodSortField) => {
    if (podFilters.sortBy !== field) return undefined;
    return podFilters.sortOrder === 'ASC' ? 'ascend' : 'descend';
  };

  const handleTableChange: TableProps<Pod>['onChange'] = (
    _pagination,
    _filters,
    sorter,
    extra,
  ) => {
    if (extra.action !== 'sort') return;
    if (!Array.isArray(sorter)) {
      if (sorter.field && sorter.order) {
        updateFilter({
          sortBy: sorter.field as PodSortField, // Safe: only podType and status columns have sorter: true
          sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC',
        });
      } else {
        updateFilter({ sortBy: undefined, sortOrder: undefined });
      }
    }
  };

  const columns: ColumnsType<Pod> = [
    {
      title: t('labels.podName'),
      dataIndex: 'podName',
      key: 'podName',
      ellipsis: { showTitle: true },
      width: '23.6%',
    },
    {
      title: t('labels.podType'),
      dataIndex: 'podType',
      key: 'podType',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('podType'),
      sortIcon: ({ sortOrder }) => (
        <CaretDownFilled
          style={{
            fontSize: 12,
            color: sortOrder ? 'var(--ant-color-primary)' : '#bfbfbf',
            transform: sortOrder === 'ascend' ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.3s, color 0.3s',
          }}
        />
      ),
      width: '8.9%',
      render: (podType: string) => {
        const config = POD_TYPE_CONFIG[podType] ?? DEFAULT_POD_TYPE_CONFIG;
        return (
          <Tag
            bordered={false}
            style={{ color: config.text, backgroundColor: config.bg }}
          >
            {config.label ? t(config.label, '') : podType}
          </Tag>
        );
      },
    },
    {
      title: t('labels.podStates'),
      dataIndex: 'states',
      key: 'states',
      width: '23.6%',
      ellipsis: { showTitle: true },
      render: (states: string[]) => (states?.length ? states.join(', ') : '-'),
    },
    {
      title: t('labels.clinicAssigned'),
      dataIndex: 'clinicCount',
      key: 'clinicCount',
      width: '14.8%',
      onCell: () => ({
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }),
      render: (count: number, record: Pod) => (
        <PodCellPopover podGuid={record.guid} type="clinics">
          <span data-cy="pod-list__clinic-count">
            {t('labels.clinicCount', { count })}
          </span>
        </PodCellPopover>
      ),
    },
    {
      title: t('labels.patientCount'),
      dataIndex: 'patientCount',
      key: 'patientCount',
      width: '8.9%',
    },
    {
      title: t('labels.members'),
      dataIndex: 'memberCount',
      key: 'memberCount',
      width: '8.9%',
      onCell: () => ({
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }),
      render: (count: number, record: Pod) => (
        <PodCellPopover podGuid={record.guid} type="members">
          <span data-cy="pod-list__member-count">{count}</span>
        </PodCellPopover>
      ),
    },
    {
      title: t('labels.status'),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      sortDirections: ['ascend', 'descend'],
      sortOrder: getSortOrder('status'),
      sortIcon: ({ sortOrder }) => (
        <CaretDownFilled
          style={{
            fontSize: 12,
            color: sortOrder ? 'var(--ant-color-primary)' : '#bfbfbf',
            transform: sortOrder === 'ascend' ? 'rotate(180deg)' : undefined,
            transition: 'transform 0.3s, color 0.3s',
          }}
        />
      ),
      width: '8.9%',
      render: (status: string) => {
        const config =
          STATUS_CONFIG[status?.toLowerCase()] ?? STATUS_CONFIG.inactive;
        return (
          <Tag
            style={{
              color: config.text,
              backgroundColor: config.bg,
              borderColor: config.border,
            }}
          >
            {t(config.label, '')}
          </Tag>
        );
      },
    },
    {
      key: 'actions',
      width: 40,
      align: 'center' as const,
      onCell: () => ({
        onClick: (e: React.MouseEvent) => e.stopPropagation(),
      }),
      render: (_: unknown, record: Pod) => (
        <PodRowActions
          pod={record}
          onEdit={handleEditPod}
          onActivate={handleActivatePod}
          onInactivate={handleInactivatePod}
          onDelete={handleDeletePod}
        />
      ),
    },
  ];

  return (
    <Layout className={s.layout}>
      <Header className={s.breadcrumbBar}>
        <Flex
          align="center"
          justify="space-between"
          className={s.breadcrumbBarInner}
        >
          <Text className={s.pageTitle}>{t('labels.podManagement')}</Text>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditPodGuid(null);
              setIsCreateModalOpen(true);
            }}
            data-cy="pod-management__create-btn"
          >
            {t('labels.createPod')}
          </Button>
        </Flex>
      </Header>

      <div className={s.content}>
        <div className={s.tableWrapper}>
          <div className={s.filterBar}>
            <div className={s.filterBarFilters}>
              <Select
                value={podFilters.podType}
                onChange={handlePodTypeChange}
                placeholder={t('labels.allTypes')}
                variant="filled"
                className={s.filterSelect}
                allowClear
                aria-label={t('labels.podType')}
                data-cy="pod-filter__type"
                options={[
                  { label: t('labels.allTypes'), value: '' },
                  { label: t('labels.rpm'), value: 'RPM' },
                  { label: t('labels.ccm'), value: 'CCM' },
                ]}
              />
              <Select
                value={podFilters.states}
                onChange={handleStatesChange}
                mode="multiple"
                placeholder={t('labels.allStates')}
                variant="filled"
                className={`${s.filterSelect} ${s.stateSelect}`}
                allowClear
                showSearch={false}
                maxTagCount={0}
                maxTagPlaceholder={(omittedValues) => {
                  const first = omittedValues[0]?.value as string;
                  const rest = omittedValues.length - 1;
                  if (rest === 0) return first;
                  return (
                    <span className={s.stateTagWrapper}>
                      <span className={s.stateTagName}>{first}</span>
                      <span className={s.stateTagCount}>+{rest}</span>
                    </span>
                  );
                }}
                menuItemSelectedIcon={null}
                popupMatchSelectWidth={300}
                popupClassName={s.stateDropdown}
                aria-label={t('labels.podStates')}
                data-cy="pod-filter__state"
                options={filteredStateOptions}
                onDropdownVisibleChange={(open) => {
                  if (!open) setStateSearch('');
                }}
                dropdownRender={(menu) => (
                  <>
                    <div className={s.stateDropdownSearch}>
                      <Input
                        placeholder={t('labels.searchStates')}
                        value={stateSearch}
                        onChange={(e) => setStateSearch(e.target.value)}
                        allowClear
                        onKeyDown={(e) => e.stopPropagation()}
                      />
                    </div>
                    {podFilters.states?.length ? (
                      <div
                        className={s.stateDropdownClearAll}
                        data-cy="pod-filter__state-clear-all"
                      >
                        <Button
                          type="link"
                          onClick={handleClearStates}
                          className={s.clearAllBtn}
                        >
                          {t('labels.clearAll')}
                        </Button>
                      </div>
                    ) : null}
                    {menu}
                  </>
                )}
                optionRender={(option) => (
                  <Flex align="center" gap={8}>
                    <Checkbox
                      checked={podFilters.states?.includes(
                        option.data.value as string,
                      )}
                      className={s.stateOption}
                    />
                    <span>{option.data.label}</span>
                  </Flex>
                )}
              />
              <Select
                value={podFilters.status}
                onChange={handleStatusChange}
                placeholder={t('labels.allStatus')}
                variant="filled"
                className={s.filterSelect}
                allowClear
                aria-label={t('labels.status')}
                data-cy="pod-filter__status"
                options={[
                  { label: t('labels.allStatus'), value: '' },
                  { label: t('labels.active'), value: 'active' },
                  { label: t('labels.inactive'), value: 'inactive' },
                ]}
              />
            </div>
            <div className={s.filterBarSearch}>
              <Input
                value={searchValue}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder={t('labels.searchPodName')}
                maxLength={100}
                className={s.searchInput}
                aria-label={t('labels.searchPodName')}
                data-cy="pod-search"
                suffix={
                  searchValue ? (
                    <CloseCircleFilled
                      className={s.searchClearIcon}
                      onClick={handleSearchClear}
                    />
                  ) : (
                    <SearchOutlined className={s.searchIcon} />
                  )
                }
              />
            </div>
          </div>
          <div className={s.tableArea}>
            {isError && (
              <div className={s.errorState}>
                <Text type="secondary">{t('labels.unableToLoadPods')}</Text>
                <Button
                  onClick={() => refetch()}
                  loading={isFetching}
                  data-cy="pod-list__retry-btn"
                >
                  {t('labels.retry')}
                </Button>
              </div>
            )}
            {!isError && (
              <Table<Pod>
                dataSource={pods}
                columns={columns}
                rowKey={(record) => record.guid}
                rowClassName={(record) =>
                  record.guid === highlightedPodGuid ? s.highlightRow : ''
                }
                loading={isLoading || isFetching}
                locale={{ emptyText }}
                onChange={handleTableChange}
                onRow={(record) => ({
                  onClick: () => handleEditPod(record),
                  style: {
                    cursor:
                      record.status === 'inactive' ? 'default' : 'pointer',
                  },
                  'aria-disabled':
                    record.status === 'inactive' ? true : undefined,
                  title:
                    record.status === 'inactive'
                      ? t('labels.inactivePodCannotEdit')
                      : undefined,
                })}
                data-cy="pod-list__table"
                pagination={{
                  current: filters.page,
                  pageSize: filters.items,
                  total: pagination?.totalCount,
                  position: ['bottomRight'],
                  showSizeChanger: false,
                  onChange: (page) => updateFilters({ page }),
                }}
              />
            )}
          </div>
        </div>
      </div>
      <CreatePodModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setEditPodGuid(null);
        }}
        onCreated={(podGuid) => setHighlightedPodGuid(podGuid)}
        editPodGuid={editPodGuid}
      />
    </Layout>
  );
};

export default PodManagement;
